import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { SettingsService } from '../../features/settings/services/settings.service';
import {
  UpdateProfileDto,
  User,
} from '../../features/settings/types/settings.types';
import { UserProfile } from '../types/profile.types';

export const getProfileQueryKey = (options?: UseProfileOptions) => {
  return ['profile', options] as const;
};

// Helper function to get profile image URL
export const getProfileImage = (profile?: UserProfile, email?: string) => {
  return (
    profile?.imageUrl ||
    `https://ui-avatars.com/api/?name=${profile?.name || email}`
  );
};

// Helper function to get profile thumbnail URL
export const getProfileThumbnail = (
  profile?: UserProfile | any,
  email?: string
) => {
  return (
    profile?.thumbnailUrl ||
    `https://ui-avatars.com/api/?name=${profile?.name || email}`
  );
};

type ProfileQueryKey = readonly ['profile', UseProfileOptions | undefined];

export interface UseProfileOptions {
  include?: string[];
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}

export const useProfile = (options?: UseProfileOptions) => {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery<User, Error, User, ProfileQueryKey>({
    queryKey: getProfileQueryKey(options),
    queryFn: () => SettingsService.getProfile(options),
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });

  const { mutate: updateProfile, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateProfileDto) => SettingsService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  const { mutate: uploadImage, isPending: isUploading } = useMutation({
    mutationFn: (file: File) => SettingsService.uploadProfileImage(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profile image updated successfully');
    },
    onError: () => {
      toast.error('Failed to upload image');
    },
  });

  return {
    getProfileImage,
    getProfileThumbnail,
    user,
    isLoading,
    error,
    updateProfile,
    isUpdating,
    uploadImage,
    isUploading,
    refetch,
    // Computed properties for convenience
    profile: user?.profile,
    settings: user?.settings,
    authDetails: user?.authDetails,
  };
};
